import { useEffect, useMemo, useState } from 'react';

import { Svgs } from 'environment';
import {
	toggleFreeLicenseLimitationModalEvent,
	LicenseLimitationErrorTypes
} from '../../../../helpers/licenses';
import { Link } from 'components/UI/Link';
import { Flex } from 'components/UI/Flex';
import { Modal } from 'components/UI/Modal';
import { Spacer } from 'components/UI/Spacer';
import { Typography } from 'components/UI/Typography';
import { useCustomEventListener } from 'helpers/events';
import { openNewTabSubscriptionPage } from 'helpers/navigation';
import { NavigationRoutes, useNavigation, useRouteMatch } from 'hooks/navigation';
import { useTranslation, useProjectId, useSubscription } from 'hooks/store';
import { ROUTES } from 'types/index';
import { useTracking } from 'app/tracking/TrackingProvider';

type NavigationType = keyof NavigationRoutes;

export function LicenseLimitationModal() {
	const { translate } = useTranslation();
	const { navigate, routes } = useNavigation();
	const { track } = useTracking();
	const [projectId] = useProjectId();

	const [licenseLimitationModalVisible, setLicenseLimitationModalVisible] = useState(false);

	const [message, setMessage] = useState('');
	const [redirectRoute, setRedirectRoute] = useState<NavigationType | null>(null);
	const [isCurrentUsersLicenseError, setIsCurrentUsersLicenseError] = useState(true);

	const isOnCollaboratorsPage = useRouteMatch([ROUTES.Collaborators, ROUTES.AddCollaborators]);

	const [
		{
			data: {
				subscriptionTypes: { isLedidiCollaborator }
			},
			loading: loadingSubscription
		}
	] = useSubscription();

	useCustomEventListener(toggleFreeLicenseLimitationModalEvent, {
		onListen: errorCode => {
			if (
				errorCode === LicenseLimitationErrorTypes.createProject ||
				errorCode === LicenseLimitationErrorTypes.copyProject
			) {
				setRedirectRoute(routes.projects.list as NavigationType);
				setMessage(translate(({ errors }) => errors.api.projects.licenseError));
				setIsCurrentUsersLicenseError(true);
				setLicenseLimitationModalVisible(true);
			}

			if (errorCode === LicenseLimitationErrorTypes.transferProject) {
				setMessage(
					translate(({ errors }) => errors.api.projects.licenseErrorProjectNewOwner)
				);
				setIsCurrentUsersLicenseError(false);
				setLicenseLimitationModalVisible(true);
			}

			if (errorCode === LicenseLimitationErrorTypes.collaboratorShareProject) {
				setMessage(
					translate(({ collaborators }) => collaborators.errors.notEligibleLicense)
				);
				projectId &&
					setRedirectRoute(
						routes.projects.collaborators.view(projectId) as NavigationType
					);
				setIsCurrentUsersLicenseError(false);
				setLicenseLimitationModalVisible(true);
			}

			if (errorCode === LicenseLimitationErrorTypes.createEntry) {
				projectId &&
					setRedirectRoute(routes.projects.dataset.view(projectId) as NavigationType);
				setMessage(translate(({ errors }) => errors.api.entries.licenseError));
				setIsCurrentUsersLicenseError(true);
				setLicenseLimitationModalVisible(true);
			}

			if (errorCode === LicenseLimitationErrorTypes.collaboratorCreateEntry) {
				projectId &&
					setRedirectRoute(routes.projects.dataset.view(projectId) as NavigationType);
				setMessage(translate(({ errors }) => errors.api.entries.collaboratorLicenseError));
				setIsCurrentUsersLicenseError(false);
				setLicenseLimitationModalVisible(true);
			}
		}
	});

	function manageSubscription() {
		track({
			eventName: 'licence_limitation_modal_upgrade_clicked'
		});
		isCurrentUsersLicenseError && openNewTabSubscriptionPage(true);
		onClose();
	}

	function onDone() {
		if (redirectRoute) navigate(redirectRoute);
		else {
			if (!isOnCollaboratorsPage) navigate(routes.projects.list);
			else projectId && navigate(routes.projects.collaborators.view(projectId));
		}
		onClose();
	}

	function onClose() {
		setLicenseLimitationModalVisible(false);
	}

	const modalTitle = useMemo(() => {
		if (!isCurrentUsersLicenseError || isLedidiCollaborator)
			return translate(dict => dict.timeToUpgradeModal.licenseLimitationHeader);
		else return translate(dict => dict.timeToUpgradeModal.header);
	}, [isCurrentUsersLicenseError, isLedidiCollaborator, loadingSubscription, translate]);

	const modalPrimaryButton = useMemo(() => {
		return {
			label: translate(dict => dict.buttons.upgrade),
			onClick: manageSubscription
		};
	}, [isCurrentUsersLicenseError, isLedidiCollaborator, loadingSubscription, translate]);

	return (
		<Modal
			title={modalTitle}
			visible={licenseLimitationModalVisible}
			primary={modalPrimaryButton}
			onClose={onDone}
			close
		>
			<Flex column align={a => a.center}>
				<Track />

				<Spacer size={s => s.xl} />
				<Svgs.UpgradePlan />
				<Spacer size={s => s.m} />
				{isCurrentUsersLicenseError ? (
					isLedidiCollaborator ? (
						<>
							<Typography.H3 multiline alignCenter>
								{translate(dict => dict.timeToUpgradeModal.licenseLimitationHeader)}
							</Typography.H3>
							<Spacer size={s => s.m} />
							<Typography.Paragraph multiline alignCenter>
								{translate(dict => dict.timeToUpgradeModal.descriptionCollaborator)}
								<Link href="mailto:support@ledidi.no">{`support@ledidi.no`}</Link>
							</Typography.Paragraph>
						</>
					) : (
						<>
							<Typography.H3 multiline alignCenter>
								{translate(dict => dict.timeToUpgradeModal.title)}
							</Typography.H3>
							<Spacer size={s => s.m} />
							<Typography.Paragraph multiline alignCenter>
								{translate(dict => dict.timeToUpgradeModal.description)}
							</Typography.Paragraph>
						</>
					)
				) : (
					<>
						<Typography.Paragraph multiline alignCenter>
							{message}
						</Typography.Paragraph>
					</>
				)}
			</Flex>
		</Modal>
	);
}

const Track = () => {
	const { track } = useTracking();

	useEffect(() => {
		track({
			eventName: 'licence_limitation_modal_shown'
		});
	}, []);

	return null;
};
